import React from "react";
import { ExternalLink, CheckCircle2 } from "lucide-react";
import { formatBRL } from "@/lib/api";
import { PixPaymentBlock } from "@/components/PixPaymentBlock";

/**
 * Shared payment modal used by LandingPage (order) and InscricaoPage (subscription).
 *
 * Props:
 *  - payment: { links: [], pix?: {}, txid?: string, isSubscription?: boolean, customerName?: string }
 *  - onClose: () => void
 *  - kind: "order" | "subscription" — drives copy and test IDs
 */
export function PaymentModal({ payment, onClose, kind = "order" }) {
  if (!payment) return null;

  // Payload from LandingPage may include `isSubscription` and `alreadyRegistered`
  // flags so we can render a clear "Inscrição" status regardless of the entry page.
  const isSub = kind === "subscription" || payment.isSubscription === true;
  const alreadyReg = payment.alreadyRegistered === true;
  const showBadge = isSub || alreadyReg;
  const modalTestId = isSub ? "sub-payment-modal" : "payment-links-modal";
  const linkTestPrefix = isSub ? "sub-pay-link" : "pay-link";
  const trackTestId = isSub ? "sub-track-order-link" : "track-order-link";
  const introText = isSub
    ? "Sua inscrição foi registrada. Escolha como pagar abaixo"
    : "Seu pedido foi registrado. Escolha como pagar abaixo";
  const trackText = isSub ? "Acompanhar minha inscrição" : "Acompanhar meu pedido";
  const badgeTitle = alreadyReg && !isSub
    ? `Você já é inscrito${payment.customerName ? ` — ${payment.customerName}` : ""}`
    : `Inscrição confirmada${payment.customerName ? ` — ${payment.customerName}` : ""}`;
  const badgeDetail = alreadyReg && !isSub
    ? "Sua inscrição prévia foi identificada por este e-mail. Efetue apenas o pagamento dos itens abaixo."
    : "Você está inscrito no 3º Encontrão Nacional Renascer. Efetue o pagamento abaixo para concluir.";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-stone-900/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        data-testid={modalTestId}
      >
        <div className="p-6 border-b border-stone-200 shrink-0">
          <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold">Pagamento</div>
          <h3 className="font-display text-2xl font-bold mt-1">Finalize seu pagamento</h3>
          <p className="text-sm text-stone-600 mt-2">
            {introText}
            {payment.pix ? " — PIX ou Stone." : "."}
          </p>
          {showBadge && (
            <div
              className="mt-4 flex items-start gap-3 p-3 rounded-xl border border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50"
              data-testid="inscricao-confirmada-badge"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900 leading-tight">
                <div className="font-bold">{badgeTitle}</div>
                <div className="text-xs text-emerald-800/80 mt-0.5">{badgeDetail}</div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {payment.pix && <PixPaymentBlock pix={payment.pix} />}
          {payment.links.length > 0 && (
            <div className="space-y-2">
              {payment.pix && (
                <div className="flex items-center gap-3 pt-2">
                  <div className="flex-1 h-px bg-stone-200" />
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                    Ou pague no cartão crédito no Stone
                  </div>
                  <div className="flex-1 h-px bg-stone-200" />
                </div>
              )}
              {payment.links.map((l, i) => (
                <a
                  key={`${l.url}-${l.unit}-${i}`}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${linkTestPrefix}-${i}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-stone-200 hover:border-orange-500 hover:bg-orange-50 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 shrink-0 rounded-full flame-gradient text-white flex items-center justify-center text-sm font-bold">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-stone-900 truncate">
                        {l.label}
                        {l.total > 1 && (
                          <span className="ml-2 text-xs text-stone-500">
                            #{l.unit}/{l.total}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-500">{formatBRL(l.amount || 0)}</div>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-orange-600 shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-stone-200 bg-stone-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
          {payment.txid && (
            <a
              href={`/pedido/${payment.txid}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={trackTestId}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-white border border-stone-300 hover:border-orange-500 hover:text-orange-700 transition-colors"
            >
              {trackText}
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="close-payment-modal"
            className="px-5 py-2 rounded-full text-sm font-semibold text-stone-700 hover:bg-white transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
